import { yupResolver } from "@hookform/resolvers/yup";
import { ChevronLeftIcon, EnterIcon } from "@radix-ui/react-icons";
import { Box, Button, Card, Flex, Grid, Heading, Text, Spinner } from "@radix-ui/themes";
import { Controller, useForm, useWatch } from "react-hook-form";
import toast from "react-hot-toast";
import { useMutation } from "react-query";
import { useNavigate } from "react-router-dom";
import TextAreaFieldComponent from "../../components/inputs/TextAreafield/TextAreafieldComponent";
import TextFieldComponent from "../../components/inputs/Textfield/TextfieldComponent";
import { routes } from "../../routes";
import { createFaculty } from "../../services/school/faculty/createFaculty";
import { CreateFacultyType } from "../../types/Faculty";
import { CreateFacultyFormValidation } from "./CreateFacultyFormValidation";
import { useGetUniversitiesQuery } from "../../hooks/useGetUniversitiesQuery";
import SelectComponent from "../../components/inputs/Select/SelectComponent";
import { provinces } from "../../utils/codes";
import React, { useEffect } from "react";

export default function CreateFaculty() {
  const navigate = useNavigate();
  const mutation = useMutation({
    mutationFn: createFaculty,
    mutationKey: "createFaculty",
  });
  const {
    data: universities,
    isLoading: isLoadingUniversities,
    isError: errorUniversities,
    refetch: refetchUniversities,
  } = useGetUniversitiesQuery({ PageNumber: 1, PageSize: 400 });

  const {
    control,
    setValue,
    handleSubmit,
    formState: { errors },
  } = useForm<CreateFacultyType>({
    resolver: yupResolver(CreateFacultyFormValidation),
    defaultValues: {
      description: "",

    },
    mode: "onBlur",
    reValidateMode: "onBlur",
    shouldUseNativeValidation: false,
  });

  const dateCreated = useWatch({ control, name: "dateCreated" });

  useEffect(() => {
    if (dateCreated) {
      const year = new Date(dateCreated).getFullYear();
      setValue("yearCreated", year, { shouldValidate: true });
    }
  }, [dateCreated, setValue]);

  const onSubmit = handleSubmit(async (data) => {
    try {
      const result = await mutation.mutateAsync(data);
      if (result) {
        toast.success("Unidade orgânica criada com sucesso!");
        navigate(routes.faculties);
      }
    } catch (err: any) {
      toast.error(`${mutation.error ?? err?.message}`);
    }
  });

  const handleProvinceChange = (idProvince: string) => {
    setValue("idProvince", idProvince);
    // refreshProvinces();
  };

  const handleBack = () => {
    navigate(routes.faculties);
  };
  return (
    <form onSubmit={onSubmit} noValidate>
      <Card size="1">
        <Flex direction="column" gap="6">
          {/* Seção 1: Dados Institucionais */}
          <Box>
            <Heading size="4" mb="4">
              Dados Institucionais
            </Heading>
            <Flex direction="column" gap="4">
              <Grid columns="2" gap="4">
                <TextFieldComponent
                  name="description"
                  label="Nome da Unidade Orgânica"
                  control={control}
                  errors={errors}
                  fieldProps={{
                    placeholder: "Ex: Faculdade de Engenharia",
                  }}
                  required
                />
                <TextFieldComponent
                  name="abbreviation"
                  label="Sigla"
                  control={control}
                  errors={errors}
                  fieldProps={{ placeholder: "Ex: FE" }}
                />
              </Grid>
              <SelectComponent
                name="idUniversity"
                label="Instituição de Ensino Superior"
                control={control}
                errors={errors}
                style={{ width: '100%' }}
                options={universities?.items.map(u => ({
                  value: u.id ?? "",
                  label: u.description ?? ""
                }))}
                itemsLoading={isLoadingUniversities}
                itemsError={errorUniversities}
                required={true}
              />

          <Grid columns="2" gap="4">
            <TextFieldComponent
              name="yearCreated"
              label="Ano de Criação"
              control={control}
              errors={errors}
              fieldProps={{ placeholder: "Ex: 2009", type: "number" }}
            />
            <TextFieldComponent
              name="dateCreated"
              label="Data de Criação"
              control={control}
              errors={errors}
              fieldProps={{ type: "date" }}
            />
          </Grid>

              <Grid columns="2" gap="4">
                <TextFieldComponent
                  name="code"
                  label="Código"
                  control={control}
                  errors={errors}
                  fieldProps={{ placeholder: "Código" }}
                />
                <TextFieldComponent
                  name="nuit"
                  label="Número Único de Identificação Tributária"
                  control={control}
                  errors={errors}
                  fieldProps={{
                    placeholder: "Número Único de Identificação Tributária",
                  }}
                />
                {/* <SelectComponent
                  name="type"
                  label="Tipo"
                  control={control}
                  errors={errors}
                  options={[
                    { value: "public", label: "Pública" },
                    { value: "private", label: "Privada" },
                  ]}
                /> */}
              </Grid>

              <Grid columns="2" gap="4">
                <SelectComponent
                  name="province"
                  label="Província"
                  control={control}
                  errors={errors}
                  options={provinces?.map((u) => ({
                    value: u.id ?? "",
                    label: u.description ?? "",
                  }))}
                  required
                />
                <TextFieldComponent
                  name="district"
                  label="Distrito"
                  control={control}
                  errors={errors}
                  fieldProps={{ placeholder: "Distrito" }}
                />
              </Grid>
            </Flex>
          </Box>
          <Box>
            <Grid columns="2" gap="4">
              <TextFieldComponent
                name="email"
                label="Email Institucional"
                control={control}
                errors={errors}
                fieldProps={{
                  type: "email",
                  placeholder: "geral@instituicao.ac.mz",
                }}
              />
              <TextFieldComponent
                name="website"
                label="Website"
                control={control}
                errors={errors}
                fieldProps={{ placeholder: "https://www.instituicao.ac.mz" }}
              />
            </Grid>
            <TextAreaFieldComponent
              name="address"
              label="Endereço"
              control={control}
              errors={errors}
              fieldProps={{
                placeholder: "Endereço completo da instituição",
                rows: 3,
              }}
            />
          </Box>
          {/* Seção 2: Contactos do Dirigente */}
          <Box>
            <Heading size="4" mb="4">
              Contactos do Dirigente
            </Heading>
            <Grid columns="3" gap="4">
              <TextFieldComponent
                name="dirigentFullname"
                label="Nome Completo"
                control={control}
                errors={errors}
                fieldProps={{ placeholder: "Nome do dirigente" }}
              />
              <TextFieldComponent
                name="dirigentPhoneNumber"
                label="Telefone"
                control={control}
                errors={errors}
                fieldProps={{ placeholder: "+258..." }}
              />
              <TextFieldComponent
                name="dirigentEmail"
                label="Email"
                control={control}
                errors={errors}
                fieldProps={{
                  type: "email",
                  placeholder: "dirigente@email.com",
                }}
              />
            </Grid>
          </Box>

          {/* Seção 3: Contactos do Responsável */}
          <Box>
            <Heading size="4" mb="4">
              Contactos do Responsável
            </Heading>
            <Grid columns="3" gap="4">
              <TextFieldComponent
                name="responsableFullname"
                label="Nome Completo"
                control={control}
                errors={errors}
                fieldProps={{ placeholder: "Nome do responsável" }}
              />
              <TextFieldComponent
                name="responsablePhoneNumber"
                label="Telefone"
                control={control}
                errors={errors}
                fieldProps={{ placeholder: "+258..." }}
              />
              <TextFieldComponent
                name="responsableEmail"
                label="Email"
                control={control}
                errors={errors}
                fieldProps={{
                  type: "email",
                  placeholder: "responsavel@email.com",
                }}
              />
            </Grid>
          </Box>

          {/* Seção 4: Contactos Gerais */}


          {/* Botões de Ação */}
          <Flex gap="4" justify="end" mt="4">
            <Button type="button" variant="soft" color="gray" onClick={handleBack}>
              <ChevronLeftIcon /> Voltar
            </Button>
            <Button type="submit" disabled={mutation.isLoading}>
              {mutation.isLoading ? (
                <Spinner />
              ) : (
                <>
                  <EnterIcon /> Registar
                </>
              )}
            </Button>
          </Flex>
        </Flex>
      </Card>
    </form>
  );
}
